import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'

// const API_BASE_URL = 'http://localhost:3001'
const API_BASE_URL = 'https://momo-survey-api.tanksolutions.asia'
// const API_BASE_URL = 'https://mymomo15.momo.vn/api'

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Track if we're currently refreshing to avoid multiple refresh requests
let isRefreshing = false
let failedQueue: Array<{
  resolve: (value?: unknown) => void
  reject: (reason?: unknown) => void
}> = []

const processQueue = (error: Error | null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve()
    }
  })

  failedQueue = []
}

// Add request interceptor for auth tokens
apiClient.interceptors.request.use((config) => {
  // Skip adding token for login endpoints
  const isLoginEndpoint =
    config.url?.includes('/auth/login') || config.url?.includes('/auth/register')

  if (!isLoginEndpoint) {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
  }

  return config
})

// Response interceptor for error handling and token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    // Handle 401 Unauthorized - token expired or invalid
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Skip token refresh for login/register endpoints
      if (
        originalRequest.url?.includes('/auth/login') ||
        originalRequest.url?.includes('/auth/register') ||
        originalRequest.url?.includes('/users/me')
      ) {
        return Promise.reject(error)
      }

      const refreshToken = localStorage.getItem('refreshToken')

      // If no refresh token, clear auth and redirect to login
      if (!refreshToken) {
        clearAuthAndRedirect()
        return Promise.reject(error)
      }

      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then(() => {
            // Retry original request with new token
            const token = localStorage.getItem('token')
            if (token && originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`
            }
            return apiClient(originalRequest)
          })
          .catch((err) => {
            return Promise.reject(err)
          })
      }

      // Mark request as retry to prevent infinite loops
      originalRequest._retry = true
      isRefreshing = true

      try {
        // Attempt to refresh the token using POST /users/me
        const response = await axios.post(
          `${API_BASE_URL}/users/me`,
          { refreshToken },
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        )

        const { token: newToken, user } = response.data

        // Update tokens in localStorage
        localStorage.setItem('token', newToken)
        localStorage.setItem('user', JSON.stringify(user))

        // Update auth store
        import('@/store/authStore').then(({ useAuthStore }) => {
          useAuthStore.getState().setAuth({
            isAuthenticated: true,
            token: newToken,
            user,
          })
        })

        // Update the authorization header for the original request
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`
        }

        // Process queued requests
        processQueue(null)
        isRefreshing = false

        // Retry the original request
        return apiClient(originalRequest)
      } catch (refreshError) {
        // Token refresh failed - clear auth and redirect
        processQueue(refreshError as Error)
        isRefreshing = false
        clearAuthAndRedirect()
        return Promise.reject(refreshError)
      }
    }

    return Promise.reject(error)
  }
)

// Helper function to clear auth and redirect to login
function clearAuthAndRedirect() {
  // Clear localStorage
  localStorage.removeItem('token')
  localStorage.removeItem('refreshToken')
  localStorage.removeItem('user')
  localStorage.removeItem('auth_provider')

  // Clear Zustand store
  import('@/store/authStore').then(({ useAuthStore }) => {
    useAuthStore.getState().clearAuth()
  })

  // Redirect to login if not already there
  if (!window.location.pathname.includes('/login')) {
    window.location.href = '/login'
  }
}

export default apiClient
